// ============================================================================
// `origem_modulo` — decodificação em UM lugar só.
//
// Um projeto de kanban nasce avulso (`origem_modulo = null`) ou espelha o board
// de um módulo (Estatutos, Eventos, Legalização). O valor é uma string curta
// gravada pelo backend (`VARCHAR(20)`), e o front precisa dela para duas
// coisas: o RÓTULO do card na listagem e a ROTA do botão "Abrir no módulo".
//
// ⚠️ Por que existir: as duas leituras estavam em arquivos diferentes, cada uma
// com a sua cadeia de `if`, e **discordavam** — `KanbanBoardContent` conhecia
// `lgz_*` (Legalização) e o `moduloLabel` de `pages/kanban/projects.tsx` não,
// então um board de legalização aparecia rotulado como "Módulo" genérico na
// listagem e como Legalização no botão. Duas cópias que precisavam concordar,
// sem nada garantindo que concordassem.
//
// Mesmo formato do registry da legalização (`Record` + resolver).
// ============================================================================

import {
  TIPO_LEGALIZACAO_LABEL,
  TIPOS_LEGALIZACAO,
} from '../../pages/societario/legalizacao/tipos';

export interface ModuloDeOrigem {
  label: string;
  /**
   * Destino do botão "Abrir no módulo". `null` = módulo sem tela própria ainda,
   * e o botão não aparece.
   */
  rota: string | null;
}

/**
 * Prefixo dos boards de legalização: o backend grava `lgz_<tipo>` porque a
 * coluna é curta. Os três tipos caem na MESMA tela, que se vira pelo filtro.
 */
const PREFIXO_LEGALIZACAO = 'lgz_';

const MODULOS: Record<string, ModuloDeOrigem> = {
  estatutos: { label: 'Estatutos', rota: '/societario/estatutos' },
  eventos: { label: 'Eventos', rota: '/eventos' },
  ...Object.fromEntries(
    TIPOS_LEGALIZACAO.map(tipo => [
      `${PREFIXO_LEGALIZACAO}${tipo}`,
      {
        label: `Legalização · ${TIPO_LEGALIZACAO_LABEL[tipo]}`,
        rota: '/societario/legalizacao',
      },
    ]),
  ),
};

/**
 * Resolve o módulo de origem. `null` para projeto avulso ou origem que ainda
 * não conhecemos.
 *
 * ⚠️ O fallback por PREFIXO cobre um `lgz_*` que o backend passe a emitir antes
 * de o front saber do tipo novo — o comportamento antigo do
 * `KanbanBoardContent` era `startsWith('lgz_')`, e perder isso faria o botão
 * sumir calado.
 */
export function resolverModuloDeOrigem(origem?: string | null): ModuloDeOrigem | null {
  if (!origem) return null;
  const exato = MODULOS[origem];
  if (exato) return exato;
  if (origem.startsWith(PREFIXO_LEGALIZACAO)) {
    return { label: 'Legalização', rota: '/societario/legalizacao' };
  }
  return null;
}

/** Rótulo do card na listagem. "Módulo" é o genérico de sempre. */
export function rotuloDoModuloDeOrigem(origem?: string | null): string {
  return resolverModuloDeOrigem(origem)?.label ?? 'Módulo';
}
